import { DEFAULT_PAGE_SIZE } from '../constants/pagination'
import { ApiResponse, Tag } from '../types'
import { apiService } from './api.service'

class TagService {
  private cache: Map<number, Tag> = new Map()
  private allTags: Tag[] | null = null

  async getCached(id: number): Promise<Tag | null> {
    // Check cache first
    if (this.cache.has(id)) {
      return this.cache.get(id)!
    }

    // If we have all tags loaded, search in them
    if (this.allTags) {
      const tag = this.allTags.find((t) => t.id === id)
      if (tag) {
        this.cache.set(id, tag)
        return tag
      }
    }

    // Otherwise fetch from API
    try {
      const response = await apiService.get<Tag>(`tags/${id}/`)
      const tag = response.data
      this.cache.set(id, tag)
      return tag
    } catch (error) {
      return null
    }
  }

  async listAll(): Promise<Tag[]> {
    if (this.allTags) {
      return this.allTags
    }

    try {
      const response = await apiService.get<{ results: Tag[] }>('tags/', {
        params: { page: 1, page_size: 100000 },
      })
      this.allTags = response.data.results

      // Cache all tags
      this.allTags.forEach((tag) => {
        this.cache.set(tag.id, tag)
      })

      return this.allTags
    } catch (error) {
      return []
    }
  }

  async getTags(params?: any): Promise<ApiResponse<Tag>> {
    try {
      const response = await apiService.get<ApiResponse<Tag>>('tags/', {
        params: params || { page_size: 100 },
      })

      // Update cache with all tags
      response.data.results.forEach((tag) => {
        this.cache.set(tag.id, tag)
      })

      this.allTags = response.data.results

      return response.data
    } catch (error) {
      return {
        count: 0,
        next: null,
        previous: null,
        results: [],
      }
    }
  }

  // Methods for ManagementList compatibility
  async listFiltered(
    page: number,
    _ordering?: string,
    sortField?: string,
    sortReverse?: boolean,
    nameFilter?: string,
    includeDocumentCount?: boolean,
    extraParams?: any
  ): Promise<ApiResponse<Tag>> {
    const params: any = {
      page,
      page_size: extraParams?.page_size ?? DEFAULT_PAGE_SIZE,
      full_perms: true, // Include full permission fields
    }

    if (sortField) {
      const prefix = sortReverse ? '-' : ''
      params.ordering = `${prefix}${sortField}`
    }

    if (nameFilter) {
      params.name__icontains = nameFilter
    }

    if (includeDocumentCount) {
      params.include_document_count = true
    }

    if (extraParams) {
      Object.assign(params, extraParams)
    }

    return this.getTags(params)
  }

  async delete(tag: Tag): Promise<void> {
    await apiService.delete(`tags/${tag.id}/`)
    this.clearCache()
  }

  async create(tag: Partial<Tag>): Promise<Tag> {
    const response = await apiService.post<Tag>('tags/', tag)
    this.clearCache()
    return response.data
  }

  async update(id: number, data: Partial<Tag>): Promise<Tag> {
    const response = await apiService.patch<Tag>(`tags/${id}/`, data)
    this.clearCache()
    return response.data
  }

  // Bulk edit for permissions
  async bulkEditObjects(objects: number[], operation: string, permissions?: any, merge?: boolean): Promise<void> {
    const params: any = {
      objects,
      object_type: 'tags',
      operation,
    }

    if (operation === 'set_permissions' && permissions) {
      params.owner = permissions.owner
      params.permissions = permissions.set_permissions
      params.merge = merge
    }

    await apiService.post('bulk_edit_objects/', params)
    this.clearCache()
  }

  clearCache(): void {
    this.cache.clear()
    this.allTags = null
  }
}

export const tagService = new TagService()
